import { Field, useFormikContext } from 'formik';
import React, { FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import { PRESET_TYPES, SYSNAME_DEFAULT } from '../../constants/preset-constants';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';
import { Filter } from '../../models/Filter';
import { ListQuery } from '../../models/ListQuery';
import { Name } from '../../models/Name';
import { PaginationModel } from '../../models/Pagination';
import { getDeviceProfileNames, getDeviceProfileTypes } from '../../services/hardwarePresetService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { requiredValidation } from '../../utilities/Validation';
import EntityHierarchy from '../entityHierarchy/entityHierarchy';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import { SelectType } from '../settingFields/SettingSelectField';

interface CardProps {
    presetType?: SelectType,
    presetId?: string,
}

export const PresetCard: FC<CardProps> = ({ presetType, presetId }) => {
    const { translate } = useLocale();
    const { values, errors, setValues } = useFormikContext<any>();
    const site = useSelector((state: IAppState) => state.site.site.label)
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const inputRef = useRef<HTMLInputElement>(null);

    useFocusOnRouteChange(inputRef);

    const getBiometricProfileFilter = (filter?: Filter[]): Filter[] => {
        const filteredBySite = appendFilter(`1,${values.id ? values.siteId : siteId}`, 'Site id', 'site.id', filter ?? [], "=in=");

        return filteredBySite;
    }
    
    const getDataMethod = () => {
        switch (presetType) {
            case SelectType.controllerProfile:
                return (listParameters?: ListQuery) => getDeviceProfileNames(PRESET_TYPES.CONTROLLER, { ...listParameters, filters: appendFilter(values.id ? values.siteId : siteId, 'Site id', 'site.id', listParameters?.filters ?? []) });
            case SelectType.readerProfile:
                return (listParameters?: ListQuery) => getDeviceProfileNames(PRESET_TYPES.READER, { ...listParameters, filters: appendFilter(values.id ? values.siteId : siteId, 'Site id', 'site.id', listParameters?.filters ?? []) });
            case SelectType.ioDeviceProfile:
                return (listParameters?: ListQuery) => getDeviceProfileNames(PRESET_TYPES.IOMODULE, { ...listParameters, filters: appendFilter(values.id ? values.siteId : siteId, 'Site id', 'site.id', listParameters?.filters ?? []) });
            case SelectType.lockProfile:
                return (listParameters?: ListQuery) => getDeviceProfileNames(PRESET_TYPES.LOCK, { ...listParameters, filters: appendFilter(values.id ? values.siteId : siteId, 'Site id', 'site.id', listParameters?.filters ?? []) });
            // case SelectType.lockerProfile:
            //     return (listParameters?: ListQuery) => getDeviceProfileNames(PRESET_TYPES.LOCKER, { ...listParameters, filters: appendFilter(values.id ? values.siteId : siteId, 'Site id', 'site.id', listParameters?.filters ?? []) });
            case SelectType.bioMetricProfile:
                return (listParameters?: ListQuery) => getDeviceProfileTypes(PRESET_TYPES.BIOMETRIC, translate, { ...listParameters, filters: getBiometricProfileFilter(listParameters?.filters) }, true); 
            default:
                return (listParameter?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => new Promise((resolve, reject) => {
                    resolve({
                        rows: new Array(0),
                        pagination: {
                            maxPageSize: 5,
                            pageNumber: 1,
                            totalCount: 0,
                            totalPages: 1,
                            sizes: [5, 50, 100],
                            pages: [1]
                        }
                    });
                })
        }
    }

    // const releaseLockersNow = async (presetId) => {
    //     try {
    //         const command: ExecuteCommand = {
    //             cmd: DEVICE,
    //             cmdSubType: RELEASE_LOCKERS_NOW,
    //             entityId: presetId,
    //         }

    //         executeLockerCommands(command)
    //             .then((response: any) => {
    //                 if (response.status === ENGINE_STATE.COMMAND_COMPLETE) {
    //                     return toast.success(translate.byKey(TranslationKeys.lockersReleased));
    //                 }
    //                 dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.cantReleaseLockersMessage) });
    //             })
    //             .catch((error) => {
    //                 dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
    //             })
    //     } catch (error) {
    //         showError(error, toast.warn);
    //     }
    // }

    const handleSelect = (itemId, item) => {
        // bio preset needs both inheritsFromId and  profileSubTypeId 
        setValues(prevValues => ({
            ...prevValues,
            inheritsFromId: itemId,
            ...(item?.subTypeId && { profileSubTypeId: item?.subTypeId })
        }));
    }

    return <div className="w-full p-2 inline-flex justify-start bg-white">
        {values.id
            ? <div className="flex flex-col ml-8 mr-5 py-6 card-details">
                <EntityHierarchy title={values.name} tree={{ label: values.site, node: { label: values.name } }} />
            </div>
            : null}
        <div className="flex flex-col py-4 px-8">
            <div className="mb-2 pr-6">
                <div className="flex flex-row justify-between" id='nameLabelId'>
                    <Label
                        htmlFor="name"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                    {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                </div>
                <Field id='nameInputFieldId'
                    type="text"
                    name="name"
                    innerRef={inputRef}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                    validate={(value) => requiredValidation(value, translate)} />
            </div>

            {/* {values.id && presetType === SelectType.lockerProfile && presetId ?
                <>
                <div id='typeDropDownBtn' className="flex flex-col flex-1 pr-3">
                    <div className="flex flex-row justify-between" id='typeLabelId'>
                        <Label htmlFor='typeId'
                            className="text-xs text-gray-600 mt-1" required={true}>{translate.byKey(TranslationKeys.type)}:</Label>
                        {errors.typeId ? <span className='text-red-600 text-xs'>{errors.typeId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.type)}
                        key={"typeId"}
                        accessor='lockerTypeId'
                        dataMethod={getLockerTypes}
                        searchAccessor={'ltName'}
                        required={true}
                        onSelect={(value) => setFieldValue('typeId', value)} />
                </div>

                <div id='releaseNowBtn' className="mt-3">
                    <button 
                        className="flex rounded-lg bg-black text-white p-3 shadow-lg text-sm" 
                        id='presetReleaseNowButton'
                        onClick={() => releaseLockersNow(presetId)}
                    >
                        {translate.byKey(TranslationKeys.releaseNow)}
                    </button>
                </div>
            </> : null
            } */}
            
            {!values.id && presetType ?
                <div id='copyOfDropDownBtn' className="flex flex-col flex-1 pr-3">
                    <div className="flex flex-row justify-between" id='inheritsFromLabelId'>
                        <Label htmlFor='pInheritsFrom.id'
                            className="text-xs text-gray-600 mt-1" required={true}>{translate.byKey(TranslationKeys.copyOf)}:</Label>
                        {errors.inheritsFromId ? <span className='text-red-600 text-xs'>{errors.inheritsFromId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.copyOf)}
                        key={"inheritsFromId"}
                        accessor='inheritsFromId'
                        dataMethod={getDataMethod()}
                        searchAccessor={'trnName'}
                        required={true}
                        onSelect={handleSelect} />
                </div> : null}
        </div>
        <div className="flex flex-col py-4 px-8">
            <div className="">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="site"
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.site)}:</Label>
                </div>
                {values.id
                    ? <Field
                        name="site"
                        disabled={true}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                    : <Field
                        value={site}
                        disabled={true}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />}
            </div>

            {/* {values.id && presetType === SelectType.lockerProfile ?
                <>
                <div className='flex flex-col flex-1 pr-3 mt-2'>
                    <Label
                        htmlFor="showAvailableLockers"
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.showAvailableLockers)}:
                    </Label>
                    <div className="my-0">
                        <Togglebox name="showAvailableLockers" label="" />
                    </div>
                    </div>
                </>
                : null
            } */}
        </div>
    </div>
};
